import { DateTime } from "luxon";

import {
  AdminCaseListSlide,
  SlideAdminList,
  Block,
  Case,
  CaseSlide,
  CaseSlideAdmin,
  LabOrder,
  Slide,
  SlideAdmin,
  Specimen,
  Stain,
} from "pwapi";

import { StainList } from "custom-models/frontLoader";

import { LabStainsState, StainsState } from "reducers/frontLoaderReducers";

import { SortedBlock, _compareBlocks } from "./blockUtils";
import { _compareSpecimens } from "./specimenUtils";
import { CaseDetailState } from "../reducers/caseDetailReducer";

type AnySlide = CaseSlide | CaseSlideAdmin | AdminCaseListSlide;

export type SlidesObj = { [key: string]: AnySlide };

export function slideStainLabel(slide: AnySlide, stain: StainList) {
  return `${slide.is_control ? "CL-" : ""}${slide.is_rescan ? "RS-" : ""}${
    stain?.abbreviation ?? ""
  }`;
}

export const slideLabel = (
  slide: AnySlide,
  block?: Block,
  specimen?: Specimen,
  stain?: StainList,
  stainInMiddle?: boolean
) => {
  const blockName = `${specimen?.name ?? ""}${block?.name ?? ""}`;
  const stainName = stain ? slideStainLabel(slide, stain) : "";
  const slideName = slide.slide_number
    ? `${slide.slide_number.match(/^\d/) ? "S" : ""}${slide.slide_number}`
    : "";

  if (!stainName)
    return `${blockName}${blockName && slideName ? "." : ""}${slideName}`;

  if (stainInMiddle)
    return `${blockName}${blockName ? " " : ""}${stainName}${
      slideName ? " " : ""
    }${slideName}`;

  return `${blockName}${blockName && slideName ? "." : ""}${slideName}${
    stainName ? " " : ""
  }${stainName}`;
};

export function caseSlideLabel(
  s: CaseSlide,
  pathCase: Case | CaseDetailState,
  stain?: StainList,
  stainInMiddle?: boolean
) {
  const block = pathCase.blocks?.find((b) => b.id === s.block_obj_id);
  const specimen = pathCase.specimens?.find((s) => s.id === block?.specimen_id);
  return slideLabel(s, block, specimen, stain, stainInMiddle);
}

export const slideDetailLabel = (
  s: Slide | SlideAdminList | SlideAdmin,
  stain?: StainList,
  stainInMiddle?: boolean
) => slideLabel(s, s.block_obj, s.specimen, stain, stainInMiddle);

export type SortedSlideBase = {
  created_at?: string;
  full_name: string;
  full_name_stain: string;
  lab_order_id?: number;
  thumbnail?: string;
};
export type SortedSlide = AnySlide & SortedSlideBase;

// DO NOT USE THIS FUNCTION
//
// _createCompareSlides() is only exported for testing purposes
export const _createCompareSlides =
  (stains: StainsState) =>
  (a: AnySlide, b: AnySlide): number => {
    const stainA = stains[a.stain_id || 0];
    const stainB = stains[b.stain_id || 0];

    // Stainless Slides are unlikely to ever actually happen outside of tests, but we put stainless Slides first just in case.
    if (stainA && !stainB) return -1;
    if (!stainA && stainB) return 1;

    // Routine Stains take precedent
    if (
      stainA?.category === Stain.category.ROUTINE &&
      stainB?.category !== Stain.category.ROUTINE
    )
      return -1;
    if (
      stainA?.category !== Stain.category.ROUTINE &&
      stainB?.category === Stain.category.ROUTINE
    )
      return 1;

    // Sort by Stain abbreviation first
    if (stainA && stainB && stainA.abbreviation !== stainB.abbreviation)
      return stainA.abbreviation.localeCompare(stainB.abbreviation, "en");

    // Control First, Rescan After, Control + Rescan First
    if (a.is_control && !b.is_control) return -1;
    if (!a.is_control && b.is_control) return 1;
    if (a.is_rescan && !b.is_rescan) return 1;
    if (!a.is_rescan && b.is_rescan) return -1;

    // Sort by slide_number last
    return (
      (a.slide_number || "").localeCompare(b.slide_number || "", "en", {
        numeric: true,
      }) || (a.id || 0) - (b.id || 0)
    );
  };

export const createCompareAdminSlides = (stains: StainsState) => {
  const compareSlides = _createCompareSlides(stains);

  return (a: SlideAdminList, b: SlideAdminList): number => {
    const specimenComp =
      a.specimen && b.specimen
        ? _compareSpecimens(a.specimen, b.specimen)
        : !a.specimen && !b.specimen
        ? 0
        : a.specimen
        ? 1
        : -1;

    const blockComp =
      a.block_obj && b.block_obj
        ? _compareBlocks(a.block_obj, b.block_obj)
        : !a.block_obj && !b.block_obj
        ? 0
        : a.block_obj
        ? 1
        : -1;

    return specimenComp || blockComp || compareSlides(a, b);
  };
};

const _mapSlideToSortedSlide = (
  slide: AnySlide,
  block?: SortedBlock,
  specimen?: Specimen,
  stain?: StainList
): SortedSlide => ({
  ...slide,
  full_name: slideLabel(slide, block, specimen),
  full_name_stain: slideLabel(slide, block, specimen, stain),
});

// DO NOT USE THIS FUNCTION DIRECTLY
//
// sortSlides() is a pure function with no memoization or caching built in.
// Instead, use functions from selectors/selectSortedCaseDetail.ts or hooks/useSortedCase.
// It is preferable to use the selectors when possible.
export const sortSlides = (
  slides: Array<AnySlide>,
  sortedBlocks: SortedBlock[],
  sortedSpecimens: Specimen[],
  stains: StainsState
): SortedSlide[] => {
  const compareSlides = _createCompareSlides(stains);

  const blockIds = sortedBlocks.map((b) => b.id);
  const orphanSlides = (slides || [])
    .filter((s) => !s.block_obj_id || !blockIds.includes(s.block_obj_id))
    .sort(compareSlides)
    .map((slide) =>
      _mapSlideToSortedSlide(
        slide,
        undefined,
        sortedSpecimens.find((s) => s.id === slide.specimen_id),
        stains[slide.stain_id || 0]
      )
    );

  const blockSlides = sortedBlocks
    .map((block) =>
      (slides || [])
        .filter((slide) => slide.block_obj_id === block.id)
        .sort(compareSlides)
        .map((slide) =>
          _mapSlideToSortedSlide(
            slide,
            block,
            sortedSpecimens.find((s) => s.id === slide.specimen_id),
            stains[slide.stain_id || 0]
          )
        )
    )
    .flat();

  return orphanSlides.concat(blockSlides);
};

/*
 * Gets the immediate next slide in a slides list
 * Gets the first slide if slide is at the end of the list
 */
export function getWrapAroundSlide(
  slides: Array<AnySlide>,
  currentSlide: AnySlide
): AnySlide | undefined {
  if (slides && slides.length > 1) {
    const currSlideIndex = slides.findIndex((s) => s.id === currentSlide.id);

    return currSlideIndex + 1 === slides.length
      ? slides[0]
      : slides[currSlideIndex + 1];
  } else {
    return undefined;
  }
}

export function sortedSpecimenSlides(
  slides: SortedSlide[] = [],
  specimenId: number,
  labOrders: LabOrder[] = [],
  stains: StainsState,
  labStains: LabStainsState
): SortedSlide[] {
  // 1. Filter by specimen_id
  const filtered = slides.filter((s) => s.specimen_id === specimenId);
  if (!filtered.length) return [];

  // 2. Sort by created_at from lab order if available (older first).
  //    If lab_order_id isn't there, tie the lab order to the stain
  //    by lab order lab_stain_id and the slide stain_id.
  //    If not available, just add the slide to the end.
  // Group slides by block_obj_id, with lower id groups first
  const slidesByBlock: { [blockId: number]: SortedSlide[] } = {};
  for (const slide of filtered) {
    const blockId = slide.block_obj_id ?? 0;
    if (!slidesByBlock[blockId]) {
      slidesByBlock[blockId] = [];
    }
    slidesByBlock[blockId].push(slide);
  }
  const blockIds = Object.keys(slidesByBlock)
    .map(Number)
    .sort((a, b) => a - b);

  const getLabOrderCreatedAt = (s: SortedSlide) => {
    if (s.lab_order_id) {
      const labOrder = labOrders.find((lo) => lo.id === s.lab_order_id);

      if (labOrder && labOrder.created_at) {
        return (
          DateTime.fromISO(labOrder.created_at).toMillis() ||
          Number.MAX_SAFE_INTEGER
        );
      }
    } else {
      const labOrder = [...labOrders]
        .sort(
          (a, b) =>
            DateTime.fromISO(b.created_at!).toMillis() -
            DateTime.fromISO(a.created_at!).toMillis()
        )
        .find(
          (lo) =>
            lo.lab_stain_id &&
            labStains[lo.lab_stain_id].stain_id === s.stain_id &&
            lo.created_at &&
            s.created_at &&
            DateTime.fromISO(lo.created_at) < DateTime.fromISO(s.created_at)
        );

      if (labOrder && labOrder.created_at) {
        return (
          DateTime.fromISO(labOrder.created_at).toMillis() ||
          Number.MAX_SAFE_INTEGER
        );
      }
    }

    // Ensure missing/invalid dates are sorted to the end
    return Number.MAX_SAFE_INTEGER;
  };

  // Sort slides by lab order created_at within each block group
  const byDate: SortedSlide[] = [];
  for (const blockId of blockIds) {
    const slidesInBlock = slidesByBlock[blockId];
    // Separate slides by stain category: ROUTINE stains first in the block
    const routineSlides: SortedSlide[] = [];
    const otherSlides: SortedSlide[] = [];
    for (const s of slidesInBlock) {
      const stain = stains[s.stain_id ?? 0];
      if (stain && stain.category === Stain.category.ROUTINE) {
        routineSlides.push(s);
      } else {
        otherSlides.push(s);
      }
    }
    // Sort both groups by lab order created_at
    routineSlides.sort(
      (a, b) => getLabOrderCreatedAt(a) - getLabOrderCreatedAt(b)
    );
    otherSlides.sort(
      (a, b) => getLabOrderCreatedAt(a) - getLabOrderCreatedAt(b)
    );

    // Put stain_id === 1 slides first, then the rest
    byDate.push(...routineSlides, ...otherSlides);
  }

  // 3. Group by full_name + stain_id
  type Group = {
    controls: SortedSlide[];
    nonControls: SortedSlide[];
    rescans: SortedSlide[];
  };
  const groups = new Map<string, Group>();
  const order: string[] = [];

  for (const s of byDate) {
    const key = `${s.full_name || ""}__${s.stain_id ?? ""}`;

    if (!groups.has(key)) {
      groups.set(key, {
        controls: [],
        nonControls: [],
        rescans: [],
      });
      order.push(key);
    }

    const g = groups.get(key)!;

    if (s.is_control) {
      g.controls.push(s);
    } else if (s.is_rescan) {
      g.rescans.push(s);
    } else {
      g.nonControls.push(s);
    }
  }

  // 4. For each group push all controls (in order) then the first non-control, then after that pass push remaining non-controls.
  const interleaved: SortedSlide[] = [];
  for (const key of order) {
    const g = groups.get(key)!;

    interleaved.push(...g.controls);

    if (g.nonControls.length) {
      interleaved.push(g.nonControls[0]);
    }

    // Push rescans at the end of each group
    if (g.rescans.length) {
      interleaved.push(...g.rescans);
    }
  }
  // Second pass: remaining non-controls for each group
  for (const key of order) {
    const g = groups.get(key)!;

    if (g.nonControls.length > 1) {
      interleaved.push(...g.nonControls.slice(1));
    }
  }

  return interleaved;
}
